import { PlusOutlined } from '@ant-design/icons';
import { Button, Divider, Input, InputRef, Select, Space } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import WarningIcon from 'assets/icons/WarningIcon';
import classNames from 'classnames';
import { showConfirmModal, showInfoModal } from 'components/Modals/CommonModals';
import { isValidAmount } from 'features/home/components/Modals/WithdrawModal';
import { onChangeAuthPage, selectUserWallets } from 'features/home/homeSlice';
import { useHandleGetWallets } from 'hooks/useHandleGetWallets';
import useUserDetail from 'hooks/useUserDetail';
import { AmountTurbo } from 'models/wallet';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatAmountV2 } from 'utils/formatAmount';
import { getLocalStorage, setLocalStorage } from 'utils/sessionStorage';
const { Option } = Select;

interface CustomAmoutnButtonProps {
  classes?: string;
  onSetField?: (value: string) => void;
  onDoubleClick?: () => void;
  onOptionDoubleClick?: (value: string) => void;
  symbol?: string;
}

interface CoinAmount {
  symbol: string;
  amounts: { value: number; label: string }[];
}
const CustomAmountButton: React.FunctionComponent<CustomAmoutnButtonProps> = ({
  classes = '',
  onSetField,
  onOptionDoubleClick,
  symbol,
}) => {
  const [customTurbo, setCustomTurbo] = useState<string>();
  const dispatch = useAppDispatch();
  const inputRef = useRef<InputRef>(null);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { coins, fetchData } = useHandleGetWallets();
  const [currentAmount, setCurrentAmount] = useState('');
  const [items, setItems] = useState<any[]>();
  const { currentUser, isUserLoggedIn } = useUserDetail();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('customAmountTurbo') && coins.length !== 0) {
      const coinAmountList = coins.map((item) => ({
        symbol: item.symbol,
        amounts: [
          {
            value: 'none',
            label: item.symbol,
          },
        ],
      }));
      localStorage.setItem('customAmountTurbo', JSON.stringify(coinAmountList));
    }
  }, [coins.length]);

  useEffect(() => {
    const localStorageData = getLocalStorage('customAmountTurbo');
    if (localStorageData) {
      const data: CoinAmount[] = JSON.parse(localStorageData);
      const currentCoinAmount = data.find((item: any) => item.symbol === symbol);
      if (currentCoinAmount && currentCoinAmount.amounts.length !== 0) {
        setCurrentAmount(currentCoinAmount.amounts[0].label);
        setItems(currentCoinAmount?.amounts || []);
      }
    }
  }, [getLocalStorage('customAmountTurbo'), symbol]);

  //add
  const addCoin = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e.preventDefault();
    if (!customTurbo) return;
    if (items?.some((x) => x.value === customTurbo)) return;
    const newItems = [...(items || []), { value: customTurbo, label: `${customTurbo}` }].sort(
      (a, b) => Number(a.value) - Number(b.value),
    );
    setItems(newItems);
    const newCoinAmount = getLocalStorage('customAmountTurbo');
    if (newCoinAmount) {
      const data: AmountTurbo[] = JSON.parse(newCoinAmount);
      if (data) {
        const newCoinArray = data.map((c) =>
          c.symbol === symbol ? { ...c, amounts: newItems } : c,
        );
        setLocalStorage('customAmountTurbo', JSON.stringify(newCoinArray));
      }
    }
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^(?!.*\..*\..*)(?=.*\d)(?!.*\.\d{9,})(?!.{11,}$)[\d.]{0,10}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setCustomTurbo(value);
    }
  };

  const handleChangeTurbo = (value: string) => {
    if (value === 'none') {
      return;
    }
    // setCurrentAmount(value);
    if (onSetField) {
      onSetField(value);
    }
    setDropdownVisible(false);
  };

  // let clickTimer: ReturnType<typeof setTimeout>;
  const handleOptionClick = (value: string) => {
    handleChangeTurbo(value.replace(',', ''));
    // clickTimer = setTimeout(() => {
    //   handleChangeTurbo(value.replace(',', ' '));
    // }, 100);
  };

  const handleOptionDoubleClick = (value: string) => {
    if (value === 'none') {
      return;
    }

    if (onOptionDoubleClick) {
      if (!isUserLoggedIn) {
        dispatch(onChangeAuthPage(0));
        return;
      }
      if (currentUser?.isTester) {
        onOptionDoubleClick(value.replace(',', ''));
        return;
      }
      if (
        !currentUser?.kyc ||
        currentUser.kyc.status === 'INPROGRESS' ||
        currentUser.kyc.status === 'RETRY'
      ) {
        showConfirmModal({
          title: 'KYC Verification Required',
          content: <p>Please complete KYC to access this feature.</p>,
          width: 360,
          icon: <WarningIcon />,
          onOk: () => {
            navigate('/kyc');
          },
          okText: 'Verify KYC',
          cancelText: 'Skip',
        });
        return;
      }
      if (currentUser.kyc.status === 'REJECTED') {
        showInfoModal({
          title: 'KYC Verification Required',
          content: <p>Your KYC has been rejected. Please contact our support team.</p>,
          width: 360,
          icon: <WarningIcon />,
        });
        return;
      }
      if (currentUser.kyc.status === 'PENDING') {
        showInfoModal({
          title: 'KYC Verification Required',
          content: <p>Your KYC is under review.</p>,
          width: 360,
          icon: <WarningIcon />,
        });
        return;
      }
      onOptionDoubleClick(value.replace(',', ''));
    }
  };

  const currentAmountList = useMemo(() => {
    return coins.find((c) => c.symbol === symbol);
  }, [symbol, coins]);

  return (
    <div className={classNames(`turbo ${classes} turbo-amount`)}>
      <div
        className="turbo-div-item"
        onClick={() =>
          handleOptionClick(formatAmountV2(currentAmountList?.minAmount, symbol === 'USD' ? 2 : 8))
        }
        onDoubleClick={() =>
          handleOptionDoubleClick(
            formatAmountV2(currentAmountList?.minAmount, symbol === 'USD' ? 2 : 8),
          )
        }
      >
        {formatAmountV2(currentAmountList?.minAmount, 8)}
      </div>
      <div
        className=" turbo-div-item"
        onClick={() =>
          handleOptionClick(
            formatAmountV2(
              ((currentAmountList?.minAmount ?? 0) + (currentAmountList?.maxAmount ?? 0)) / 2,
              symbol === 'USD' ? 2 : 8,
            ),
          )
        }
        onDoubleClick={() =>
          handleOptionDoubleClick(
            formatAmountV2(
              ((currentAmountList?.minAmount ?? 0) + (currentAmountList?.maxAmount ?? 0)) / 2,
              symbol === 'USD' ? 2 : 8,
            ),
          )
        }
      >
        {formatAmountV2(
          ((currentAmountList?.minAmount ?? 0) + (currentAmountList?.maxAmount ?? 0)) / 2,
          symbol === 'USD' ? 2 : 8,
        )}
      </div>
      <div
        className="turbo-div-item"
        onClick={() =>
          handleOptionClick(formatAmountV2(currentAmountList?.maxAmount, symbol === 'USD' ? 2 : 8))
        }
        onDoubleClick={() =>
          handleOptionDoubleClick(
            formatAmountV2(currentAmountList?.maxAmount, symbol === 'USD' ? 2 : 8),
          )
        }
      >
        {formatAmountV2(currentAmountList?.maxAmount, symbol === 'USD' ? 2 : 8)}
      </div>

      {/* <Select
        className="turbo-dropdown primary-text-color turbo-div-item"
        dropdownClassName="asset-dropdown primary-text-color"
        value={currentAmount}
        onChange={handleChangeTurbo}
        onDropdownVisibleChange={(visible) => setDropdownVisible(visible)}
        open={dropdownVisible}
        dropdownRender={(turbo) => (
          <>
            <div>
              {items?.map((item) => (
                <div
                  key={item.value}
                  onClick={() => handleOptionClick(item.value)}
                  onDoubleClick={() => handleOptionDoubleClick(item.value)}
                  style={{ padding: '8px ', cursor: 'pointer' }}
                >
                  <div>{`${item.label}`}</div>
                </div>
              ))}
            </div>
            <Divider style={{ margin: '4px 0' }} />
            <Space style={{ padding: '0 4px 4px' }}>
              <Input
                ref={inputRef}
                type="text"
                name="amount"
                id="amount"
                autoComplete="off"
                onChange={onChange}
                value={customTurbo}
              />
              <Button type="text" icon={<PlusOutlined />} onClick={addCoin}></Button>
            </Space>
            <div className="input-turbo"></div>
          </>
        )}
      >
        {items?.map((item) => (
          <Option key={item.value} value={item.value}>
            <div>{`${item.label}`}</div>
          </Option>
        ))}
      </Select> */}
    </div>
  );
};

export default CustomAmountButton;
